/**
 * FactorialLevels
 * ---------------
 *
 * Pure helpers for computing/labeling Factorial experiment levels.
 *
 * August 2026
 */

// Maximum number of factorial levels the UI will allow. Trial count grows as
// levels^parameters, so this keeps accidental combinatorial explosions in check.

// John: While theoretically there are no limits to levels,  the typical limit is 3 (rarely over 4).
// Offering high, med and  low is good for our use cases.
export const MAX_FACTORIAL_LEVELS = 4;

// Percentage offset below nominal used for the lowest ("Low") Factorial
// level. Levels in between (if any) are spaced evenly up to nominal, which
// is always the top level: 2 levels -> [-20%, nominal]; 3 levels ->
// [-20%, -10%, nominal].
const FACTORIAL_LOW_OFFSET = -0.2;

/**
 * Looks up the current constant value backing a parameter in the model, if
 * it has one.
 *
 * @param {Object} param  - A parameter row (from availableParams).
 * @param {Object} config - The simulation model's configuration object.
 */
export function getNominalConstantValue(param, config) {
  if (param.type === "entity_type") {
    const dist = config.entity_types?.[param.gid]?.[param.parameter];
    return dist?.dist === "constant" ? (dist.attr?.value ?? null) : null;
  }

  if (param.type === "action") {
    if (param.parameter === "time") {
      const dist = config.actions?.[param.gid]?.time;
      return dist?.dist === "constant" ? (dist.attr?.value ?? null) : null;
    }
    if (param.parameter === "request_amount") {
      // Resource request amounts are always a plain fixed number, not a
      // distribution -- so they're always "constant".
      const amount =
        config.actions?.[param.gid]?.resources?.[param.param_arg]
          ?.request_amount;
      return typeof amount === "number" ? amount : null;
    }
  }

  if (param.type === "resource") {
    // Resource amounts are always a plain fixed number too.
    const amount = config.resources?.[param.gid]?.initialAmount;
    return typeof amount === "number" ? amount : null;
  }

  return null;
}

/**
 * Computes default Factorial level values for a parameter with a known
 * nominal (constant) value. The lowest level is always 20% below nominal;
 * any levels in between are spaced evenly up to nominal itself, the top
 * level. Examples: 2 levels -> [-20%, nominal]; 3 levels ->
 * [-20%, -10%, nominal].
 *
 * @param {Number} nominal   - The parameter's current constant value.
 * @param {Number} numLevels - Number of Factorial levels configured.
 * @returns {Array<Number>|null} One value per level, or null if there's no nominal to work from.
 */
export function computeDefaultFactorialLevels(nominal, numLevels) {
  if (
    nominal === null ||
    nominal === undefined ||
    !numLevels ||
    numLevels < 2
  ) {
    return null;
  }

  const step = -FACTORIAL_LOW_OFFSET / (numLevels - 1);
  return Array.from({ length: numLevels }, (_, i) => {
    const offset = FACTORIAL_LOW_OFFSET + step * i;
    return Math.round(nominal * (1 + offset) * 100) / 100;
  });
}

/**
 * Returns a level's guaranteed [min, max] range, or null if it doesn't have
 * one.
 *
 * @param {Number|Object|null} level - A single factorial level's value.
 */
export function getLevelBounds(level) {
  if (level === null || level === undefined || level === "") {
    return null;
  }
  if (typeof level !== "object") {
    return Number.isFinite(level) ? { min: level, max: level } : null;
  }
  if (level.dist === "uniform" || level.dist === "triangular") {
    if (level.min === null || level.max === null) {
      return null;
    }
    return { min: level.min, max: level.max };
  }
  return null;
}

/**
 * Builds display labels for each factorial level.
 * A 2-level experiment uses the familiar "Low" / "High" convention;
 * A 3 level experiment uses low/mid/high convention;
 * anything else falls back to "Level 1", "Level 2", etc.
 *
 * @param {Number} numLevels - Number of levels configured for the experiment.
 */
export function getLevelLabels(numLevels) {
  if (numLevels === 2) {
    return ["Low", "High"];
  } else if (numLevels === 3) {
    return ["Low", "Medium", "High"];
  }
  return Array.from({ length: numLevels }, (_, i) => `Level ${i + 1}`);
}
